import { Trans } from '@lingui/macro'
import { Button } from '@jotta/ui/Button'
import { useEffect, useState } from 'react'
import { bizApi } from '../BizApi'
import { ChallengeDialog } from '@jotta/ui/ChallengeDialog'
import { useNavigateAfterTerminate } from '@jotta/router'
import SvgTrash from '@jotta/ui/SvgTrash'
import { TerminationSurveyCategory } from '@jotta/grpc-connect-openapi/customer'
import { ModalDialog } from '@jotta/ui/ModalDialog'
import { ReactAriaCheckbox } from '@jotta/ui/ReactAriaCheckbox'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

type TerminationState = 'idle' | 'confirmation' | 'survey' | 'challenges'

export function BizActionButtonTerminateAccount() {
  const [survey, setSurvey] = useState<string | undefined>(undefined)
  const [state, setState] = useState<TerminationState>('idle')
  const onClose = () => setState('idle')

  return (
    <>
      <Button
        icon={SvgTrash}
        onClick={() => setState('confirmation')}
        className="btn btn-destructive"
      >
        <Trans>Terminate account</Trans>
      </Button>
      <ModalDialog onClose={onClose} open={state !== 'idle'}>
        <Dialog
          state={state}
          setState={setState}
          survey={survey}
          setSurvey={setSurvey}
          onClose={onClose}
        />
      </ModalDialog>
    </>
  )
}

function Dialog({
  state,
  setState,
  survey,
  setSurvey,
  onClose,
}: {
  state: TerminationState
  setState: (state: TerminationState) => void
  survey: string | undefined
  setSurvey: (survey: string) => void
  onClose: () => void
}) {
  const [consent, setConsent] = useState(false)
  const navigateAfterTerminate = useNavigateAfterTerminate()
  const { data: terminateResponse, mutateAsync } =
    bizApi.company.terminate.useMutation({})
  const challenges = terminateResponse?.challenges

  useEffect(() => {
    if (challenges?.challenges.length === 0) {
      navigateAfterTerminate()
    }
  }, [challenges, navigateAfterTerminate])

  const {
    data: { company },
  } = bizApi.company.get.useSuspenseQuery()
  const companyName = company.name

  switch (state) {
    case 'idle':
      return null
    case 'confirmation':
      return (
        <>
          <div className="title-md">
            <Trans>Permanently delete {companyName}?</Trans>
          </div>
          <div className="body-md-ln pt-8">
            <Trans>
              This will permanently delete all users accounts and the business
              account itself {companyName}. All files and users associated with
              this accounts will be deleted permanently.
            </Trans>
          </div>
          <div className="form pt-4">
            <ReactAriaCheckbox isSelected={consent} onChange={setConsent}>
              <Trans>
                I understand this operation is permanent and cannot be undone
              </Trans>
            </ReactAriaCheckbox>
          </div>
          <div className="flex justify-end gap-2 pt-6">
            <Button className="btn btn-outline" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              className="btn btn-destructive"
              onClick={() => setState('survey')}
              disabled={!consent}
            >
              <Trans>Continue</Trans>
            </Button>
          </div>
        </>
      )
    case 'survey':
      return (
        <>
          <div className="title-md">
            <Trans>Help us improve</Trans>
          </div>
          <div className="body-md-ln pt-6">
            <Trans>
              What is the primary reason for cancelling your subscription?
            </Trans>
          </div>
          <div className="pt-6">
            <textarea
              className="textarea min-h-24 w-full"
              value={survey}
              onChange={event => {
                setSurvey(event.target.value)
              }}
            />
          </div>
          <div className="flex justify-end gap-2 pt-6">
            <Button
              className="btn btn-outline"
              onClick={() => setState('confirmation')}
            >
              <Trans>Back</Trans>
            </Button>
            <Button
              className="btn btn-destructive"
              onClick={() => setState('challenges')}
            >
              <Trans>Continue</Trans>
            </Button>
          </div>
        </>
      )
    case 'challenges':
      return (
        <ChallengeDialog
          requestType="destructive"
          challenges={challenges}
          onRequest={credentials =>
            mutateAsync({
              credentials,
              survey:
                survey !== undefined && survey !== null && survey.trim() !== ''
                  ? {
                      comment: survey,
                      category:
                        TerminationSurveyCategory.SURVEY_CATEGORY_UNKNOWN,
                    }
                  : undefined,
            })
          }
          onClose={onClose}
        />
      )
    default:
      exhaustiveGuard(state)
  }
}
